import React from 'react';
import { Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useI18n } from '../contexts/I18nContext';

const CARRIER_OPTIONS = ['MSC', 'EVERGREEN', 'COSCO', 'CMA', 'HAPAG', 'ONE', 'MAERSK', 'ZIM', 'YANG MING'];
const TERMINAL_OPTIONS = [
  { code: 'CONTECON', label: 'Contecon (TEC II)' },
  { code: 'TIMSA', label: 'TIMSA / Smartport' },
  { code: 'SSA', label: 'SSA (TEC I)' },
];

/**
 * Self-contained advanced-filters block for ShipmentsPage.
 *
 * Receives the current filters object and a handler for changes. The parent
 * still owns the filter state and "apply" logic — this component only renders
 * the UI and emits change events (no persistence side-effects).
 */
export function AdvancedFiltersPanel({ filters, onFilterChange, onSelectFilterChange, onClear }) {
  const { t } = useI18n();

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="advanced-filters-panel">
      <div className="flex items-center gap-2 mb-3">
        <Filter size={14} className="text-muted-foreground" />
        <span className="text-sm font-medium text-muted-foreground">{t('shipments.advancedFilters')}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">{t('shipments.carrier')}</Label>
          <Select value={filters.carrier_code} onValueChange={(v) => onSelectFilterChange('carrier_code', v)}>
            <SelectTrigger data-testid="filter-carrier"><SelectValue placeholder={t('shipments.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('shipments.all')}</SelectItem>
              {CARRIER_OPTIONS.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">{t('shipments.terminal')}</Label>
          <Select value={filters.terminal_code} onValueChange={(v) => onSelectFilterChange('terminal_code', v)}>
            <SelectTrigger data-testid="filter-terminal"><SelectValue placeholder={t('shipments.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('shipments.all')}</SelectItem>
              {TERMINAL_OPTIONS.map(({ code, label }) => (<SelectItem key={code} value={code}>{label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">{t('shipments.customsAgent')}</Label>
          <Input
            placeholder={t('shipments.partialMatch')}
            value={filters.agente_aduanal}
            onChange={(e) => onFilterChange('agente_aduanal', e.target.value)}
            data-testid="filter-agente-aduanal"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">{t('shipments.customsTrafficLight')}</Label>
          <Select value={filters.semaforo} onValueChange={(v) => onSelectFilterChange('semaforo', v)}>
            <SelectTrigger data-testid="filter-semaforo"><SelectValue placeholder={t('shipments.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('shipments.all')}</SelectItem>
              <SelectItem value="ROJO">{t('shipments.redLight')}</SelectItem>
              <SelectItem value="VERDE">{t('shipments.greenLight')}</SelectItem>
              <SelectItem value="SIN_DATOS">{t('shipments.noLight')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">{t('shipments.pedimento')}</Label>
          <Select value={filters.has_pedimento} onValueChange={(v) => onSelectFilterChange('has_pedimento', v)}>
            <SelectTrigger data-testid="filter-has-pedimento"><SelectValue placeholder={t('shipments.all')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('shipments.all')}</SelectItem>
              <SelectItem value="yes">{t('shipments.withPedimento')}</SelectItem>
              <SelectItem value="no">{t('shipments.withoutPedimento')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">{t('shipments.delayInPort')}</Label>
          <Input
            type="number"
            min="0"
            placeholder="ej. 5"
            value={filters.min_days_in_port}
            onChange={(e) => onFilterChange('min_days_in_port', e.target.value)}
            data-testid="filter-min-days-port"
          />
        </div>
      </div>
      <div className="flex justify-end mt-3 gap-2">
        <Button variant="ghost" size="sm" onClick={onClear} data-testid="clear-advanced-filters">
          {t('shipments.clearAdvanced')}
        </Button>
      </div>
    </div>
  );
}
